import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import PageHeader from "../../components/PageHeader";
import Page from "../../types/Page";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  AccommodationQueryFilters,
  getAccommodationsAsync,
  selectAccommodationItems,
  selectAccommodationsLoadingStatus,
} from "../AccommodationsPage/accommodationsSlice";
import { LOADING_STATUS } from "../../common/constants";
import { useAuthUser } from "../../features/auth/authHooks";
import { InitialAllAccommodationsPaginationParameters } from "../../types/pagination.types";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { formatUtcAsFinnishDateTime } from "../../common/dateUtils";

type DistributionChannel = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  isActive?: boolean;
};

type AccommodationIcalImport = {
  id?: string;
  sourceUrl: string;
  fetchCount: number;
  feedStatus: string | number;
  importedAtUtc?: string | null;
  successfulImportAtUtc?: string | null;
  isActive: boolean;
  accommodationId: string;
  distributionChannelId: string;
};

type AccommodationIcalExport = {
  id?: string;
  feedPublicId: string;
  requestCount: number;
  lastRequestIp?: string | null;
  feedStatus: string | number;
  exportedAtUtc?: string | null;
  successfulExportAtUtc?: string | null;
  isActive: boolean;
  accommodationId: string;
  distributionChannelId: string;
  exportUrl?: string | null;
};

type ImportFormState = {
  distributionChannelId: string;
  sourceUrl: string;
  isActive: boolean;
};

type ExportFormState = {
  distributionChannelId: string;
  isActive: boolean;
};

const IcalIntegrationsPageType: Page = {
  ActiveTab: "ical-integrations",
  DisplayName: "iCal",
  LinkUrl: "/ical-integrations",
  Path: "/ical-integrations",
  RouteName: "ical-integrations",
};

const IcalIntegrationsPage = () => {
  const dispatch = useAppDispatch();
  const { ownerAccountId } = useAuthUser();
  const accommodations = useAppSelector(selectAccommodationItems);
  const accommodationsLoadingStatus = useAppSelector(
    selectAccommodationsLoadingStatus,
  );

  const [filtering] = useState<AccommodationQueryFilters>({
    PageNumber: InitialAllAccommodationsPaginationParameters.PageNumber,
    PageSize: InitialAllAccommodationsPaginationParameters.PageSize,
    filterCriteria: {
      showDeleted: false,
      searchString: undefined,
      accommodationNumber: undefined,
      novelty: undefined,
      showInWebShop: undefined,
      showInOwnerPage: undefined,
      accommodationGroupId: undefined,
      accountId: ownerAccountId,
    },
  });

  const [distributionChannels, setDistributionChannels] = useState<
    DistributionChannel[]
  >([]);
  const [icalImports, setIcalImports] = useState<AccommodationIcalImport[]>([]);
  const [icalExports, setIcalExports] = useState<AccommodationIcalExport[]>([]);
  const [icalError, setIcalError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [importForms, setImportForms] = useState<
    Record<string, ImportFormState>
  >({});
  const [exportForms, setExportForms] = useState<
    Record<string, ExportFormState>
  >({});
  const [savingImports, setSavingImports] = useState<Record<string, boolean>>(
    {},
  );
  const [savingExports, setSavingExports] = useState<Record<string, boolean>>(
    {},
  );
  const [deletingImports, setDeletingImports] = useState<
    Record<string, boolean>
  >({});
  const [deletingExports, setDeletingExports] = useState<
    Record<string, boolean>
  >({});
  const [fetchingImports, setFetchingImports] = useState<
    Record<string, boolean>
  >({});
  const [copiedExportId, setCopiedExportId] = useState<string | null>(null);

  const handleCopyExportUrl = (url: string, exportId: string) => {
    navigator.clipboard.writeText(url);
    setCopiedExportId(exportId);
    setTimeout(() => setCopiedExportId(null), 1500);
  };

  const normalizeArray = (value: any) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.result)) return value.result;
    if (Array.isArray(value?.result?.items)) return value.result.items;
    return [];
  };

  const initFetch = useCallback(() => {
    dispatch(getAccommodationsAsync(filtering));
  }, [dispatch, ownerAccountId]);

  const fetchIcalData = useCallback(async () => {
    setIcalError(null);
    try {
      // Fetch distribution channels first
      const channelsResponse = await apiService.get(
        API_ENDPOINTS.ICAL.GET_DISTRIBUTION_CHANNELS,
      );
      setDistributionChannels(normalizeArray(channelsResponse));

      // Fetch imports and exports for each accommodation
      if (accommodations.length > 0) {
        const importsPromises = accommodations.map((acc) =>
          apiService.get(API_ENDPOINTS.ICAL.GET_IMPORTS, {
            params: { accommodationId: acc.id },
          }),
        );
        const exportsPromises = accommodations.map((acc) =>
          apiService.get(API_ENDPOINTS.ICAL.GET_EXPORTS, {
            params: { accommodationId: acc.id },
          }),
        );

        const [importsResponses, exportsResponses] = await Promise.all([
          Promise.all(importsPromises),
          Promise.all(exportsPromises),
        ]);

        // Flatten the arrays since each response is for one accommodation
        const allImports = importsResponses.flatMap((response) =>
          normalizeArray(response),
        );
        const allExports = exportsResponses.flatMap((response) =>
          normalizeArray(response),
        );

        setIcalImports(allImports);
        setIcalExports(allExports);
      } else {
        setIcalImports([]);
        setIcalExports([]);
      }
    } catch (error) {
      setIcalError("iCal tietojen haku epaonnistui.");
    }
  }, [accommodations]);

  useEffect(() => {
    initFetch();
  }, [initFetch, ownerAccountId]);

  useEffect(() => {
    fetchIcalData();
  }, [fetchIcalData]);

  const sortedAccommodations = useMemo(() => {
    return accommodations.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [accommodations]);

  const getDistributionChannelName = useCallback(
    (id: string) => {
      const channel = distributionChannels.find((item) => item.id === id);
      return channel ? channel.name : "Tuntematon";
    },
    [distributionChannels],
  );

  const renderFeedStatusBadge = (status: string | number) => {
    const numericStatus =
      typeof status === "number" ? status : Number.parseInt(status, 10);
    if (numericStatus === 1) {
      return <Badge bg="success">Ok</Badge>;
    }
    if (numericStatus === 2) {
      return <Badge bg="danger">Virhe</Badge>;
    }
    return <Badge bg="secondary">Uusi</Badge>;
  };

  const findImportByChannel = (
    accommodationId: string,
    distributionChannelId: string,
  ) => {
    if (!distributionChannelId) return undefined;
    return icalImports.find(
      (item) =>
        item.accommodationId === accommodationId &&
        item.distributionChannelId === distributionChannelId,
    );
  };

  const findExportByChannel = (
    accommodationId: string,
    distributionChannelId: string,
  ) => {
    if (!distributionChannelId) return undefined;
    return icalExports.find(
      (item) =>
        item.accommodationId === accommodationId &&
        item.distributionChannelId === distributionChannelId,
    );
  };

  const getImportForm = (accommodationId: string): ImportFormState => {
    return (
      importForms[accommodationId] ?? {
        distributionChannelId: "",
        sourceUrl: "",
        isActive: true,
      }
    );
  };

  const getExportForm = (accommodationId: string): ExportFormState => {
    return (
      exportForms[accommodationId] ?? {
        distributionChannelId: "",
        isActive: true,
      }
    );
  };

  const updateImportForm = (
    accommodationId: string,
    updates: Partial<ImportFormState>,
  ) => {
    setImportForms((prev) => ({
      ...prev,
      [accommodationId]: {
        ...getImportForm(accommodationId),
        ...updates,
      },
    }));
  };

  const updateExportForm = (
    accommodationId: string,
    updates: Partial<ExportFormState>,
  ) => {
    setExportForms((prev) => ({
      ...prev,
      [accommodationId]: {
        ...getExportForm(accommodationId),
        ...updates,
      },
    }));
  };

  const handleAddImport = async (accommodationId: string) => {
    const form = getImportForm(accommodationId);
    const existingImport = findImportByChannel(
      accommodationId,
      form.distributionChannelId,
    );
    if (!form.distributionChannelId || !form.sourceUrl) {
      return;
    }
    setSavingImports((prev) => ({ ...prev, [accommodationId]: true }));
    setActionError(null);
    try {
      const payload = {
        id: existingImport?.id,
        accommodationId,
        distributionChannelId: form.distributionChannelId,
        sourceUrl: form.sourceUrl,
        isActive: form.isActive,
      };

      await apiService.post(API_ENDPOINTS.ICAL.SAVE_IMPORT, payload);

      await fetchIcalData();
    } catch (error) {
      setActionError("iCal tuonnin tallennus epaonnistui.");
    } finally {
      setSavingImports((prev) => ({ ...prev, [accommodationId]: false }));
    }
  };

  const handleAddExport = async (accommodationId: string) => {
    const form = getExportForm(accommodationId);
    const existingExport = findExportByChannel(
      accommodationId,
      form.distributionChannelId,
    );
    if (!form.distributionChannelId) {
      return;
    }
    setSavingExports((prev) => ({ ...prev, [accommodationId]: true }));
    setActionError(null);
    try {
      const payload = {
        id: existingExport?.id,
        accommodationId,
        distributionChannelId: form.distributionChannelId,
        isActive: form.isActive,
      };

      await apiService.post(API_ENDPOINTS.ICAL.SAVE_EXPORT, payload);

      await fetchIcalData();
    } catch (error) {
      setActionError("iCal viennin tallennus epaonnistui.");
    } finally {
      setSavingExports((prev) => ({ ...prev, [accommodationId]: false }));
    }
  };

  const handleDeleteImport = async (importId: string) => {
    if (
      !window.confirm(
        "Haluatko varmasti poistaa tämän kalenteri-integraation?\n\n" +
          "Huomioithan, että poiston jälkeen:\n" +
          "- Kanavan kautta tuodut estetyt jaksot (kalenteritapahtumat) poistuvat\n" +
          "- Varaukset on jatkossa lisättävä manuaalisesti omaliittymään omina varauksina, jos haluat säilyttää varaustilanteen synkronoituna",
      )
    ) {
      return;
    }
    setDeletingImports((prev) => ({ ...prev, [importId]: true }));
    setActionError(null);
    try {
      await apiService.delete(
        `${API_ENDPOINTS.ICAL.DELETE_IMPORT}/${importId}`,
      );
      await fetchIcalData();
    } catch (error) {
      setActionError("iCal tuonnin poisto epäonnistui.");
    } finally {
      setDeletingImports((prev) => ({ ...prev, [importId]: false }));
    }
  };

  const handleDeleteExport = async (exportId: string) => {
    if (
      !window.confirm(
        "Haluatko varmasti poistaa tämän kalenteriviennin?\n\n" +
          "Poiston jälkeen varauksia ei enää päivitetä niihin ulkoisiin järjestelmiin, jotka käyttävät tätä kalenterisyötettä.",
      )
    ) {
      return;
    }
    setDeletingExports((prev) => ({ ...prev, [exportId]: true }));
    setActionError(null);
    try {
      await apiService.delete(
        `${API_ENDPOINTS.ICAL.DELETE_EXPORT}/${exportId}`,
      );
      await fetchIcalData();
    } catch (error) {
      setActionError("iCal viennin poisto epäonnistui.");
    } finally {
      setDeletingExports((prev) => ({ ...prev, [exportId]: false }));
    }
  };

  const handleFetchImport = async (
    importId: string,
    accommodationNumber: string,
    distributionChannelId: string,
  ) => {
    setFetchingImports((prev) => ({ ...prev, [importId]: true }));
    setActionError(null);
    try {
      await apiService.post(
        `${API_ENDPOINTS.ICAL.IMPORT_ICAL}/${accommodationNumber}`,
        {},
        { params: { distributionChannelId } },
      );
      await fetchIcalData();
    } catch (error) {
      setActionError("iCal tuonnin haku epäonnistui.");
    } finally {
      setFetchingImports((prev) => ({ ...prev, [importId]: false }));
    }
  };

  const renderExportRows = (accommodationId: string) => {
    const rows = icalExports.filter(
      (item) => item.accommodationId === accommodationId,
    );
    if (rows.length === 0) {
      return <div className="text-muted small">Ei vientejä.</div>;
    }
    return (
      <Table responsive size="sm" className="align-middle mb-0">
        <thead>
          <tr>
            <th>Kanava</th>
            <th>Tila</th>
            <th>Aktiivinen</th>
            <th>Haettu</th>
            <th>Onnistunut</th>
            <th>Vientejä</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const itemId = item.id ?? item.feedPublicId;
            const isDeleting = deletingExports[itemId];
            return (
              <tr key={itemId}>
                <td>
                  {getDistributionChannelName(item.distributionChannelId)}
                </td>
                <td>{renderFeedStatusBadge(item.feedStatus)}</td>
                <td>{item.isActive ? "Kyllä" : "Ei"}</td>
                <td>{formatUtcAsFinnishDateTime(item.exportedAtUtc)}</td>
                <td>
                  {formatUtcAsFinnishDateTime(item.successfulExportAtUtc)}
                </td>
                <td>{item.requestCount ?? 0}</td>
                <td>
                  <div className="d-flex gap-8">
                    {item.exportUrl && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() =>
                          handleCopyExportUrl(item.exportUrl!, itemId)
                        }
                      >
                        {copiedExportId === itemId
                          ? "Kopioitu!"
                          : "Kopioi osoite"}
                      </Button>
                    )}
                    {item.id &&
                      (!item.successfulExportAtUtc ||
                        (item.requestCount ?? 0) < 10) && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeleteExport(item.id!)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Poistetaan..." : "Poista"}
                        </Button>
                      )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const renderImportRows = (
    accommodationId: string,
    accommodationNumber: string,
  ) => {
    const rows = icalImports.filter(
      (item) => item.accommodationId === accommodationId,
    );
    if (rows.length === 0) {
      return <div className="text-muted small">Ei tuonteja.</div>;
    }
    return (
      <Table responsive size="sm" className="align-middle mb-0">
        <thead>
          <tr>
            <th>Kanava</th>
            <th>Lahde-URL</th>
            <th>Tila</th>
            <th>Aktiivinen</th>
            <th>Tuotu</th>
            <th>Onnistunut</th>
            <th>Tuonteja</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const itemId = item.id ?? item.sourceUrl;
            const isDeleting = item.id ? deletingImports[item.id] : false;
            const isFetching = item.id ? fetchingImports[item.id] : false;
            return (
              <tr key={itemId}>
                <td>
                  {getDistributionChannelName(item.distributionChannelId)}
                </td>
                <td className="text-break">{item.sourceUrl}</td>
                <td>{renderFeedStatusBadge(item.feedStatus)}</td>
                <td>{item.isActive ? "Kyllä" : "Ei"}</td>
                <td>{formatUtcAsFinnishDateTime(item.importedAtUtc)}</td>
                <td>
                  {formatUtcAsFinnishDateTime(item.successfulImportAtUtc)}
                </td>
                <td>{item.fetchCount ?? 0}</td>
                <td>
                  <div className="d-flex gap-8">
                    {item.id && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() =>
                          handleFetchImport(
                            item.id!,
                            accommodationNumber,
                            item.distributionChannelId,
                          )
                        }
                        disabled={!item.isActive || isFetching}
                      >
                        {isFetching ? "Haetaan..." : "Tuo iCal"}
                      </Button>
                    )}
                    {item.id &&
                      (!item.successfulImportAtUtc ||
                        (item.fetchCount ?? 0) < 10) && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeleteImport(item.id!)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Poistetaan..." : "Poista"}
                        </Button>
                      )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <div className={IcalIntegrationsPageType.RouteName}>
      <Layout CurrentPage={IcalIntegrationsPageType}>
        <PageHeader
          Title={IcalIntegrationsPageType.DisplayName}
          SubTitle={IcalIntegrationsPageType.DisplayParentName}
          Back={false}
          Icon="calendar3"
        />

        {icalError && <div className="alert alert-danger">{icalError}</div>}
        {actionError && <div className="alert alert-danger">{actionError}</div>}

        {accommodationsLoadingStatus !== LOADING_STATUS.IDLE && (
          <div className="d-flex gap-16 align-items-center mb-24">
            <Spinner animation="border" />
            <span>Ladataan kohteita...</span>
          </div>
        )}

        {accommodationsLoadingStatus === LOADING_STATUS.IDLE &&
          sortedAccommodations.length === 0 && (
            <div className="alert alert-primary">Ei kohteita.</div>
          )}

        <div className="d-grid gap-24">
          {sortedAccommodations.map((accommodation) => {
            const importForm = getImportForm(accommodation.id);
            const exportForm = getExportForm(accommodation.id);
            const isSavingImport = savingImports[accommodation.id];
            const isSavingExport = savingExports[accommodation.id];
            const existingImport = findImportByChannel(
              accommodation.id,
              importForm.distributionChannelId,
            );
            const existingExport = findExportByChannel(
              accommodation.id,
              exportForm.distributionChannelId,
            );
            const importButtonText = existingImport
              ? "Päivitä tuontikanava"
              : "Lisää tuontikanava";
            const exportButtonText = existingExport
              ? "Päivitä vientikanava"
              : "Lisää vientikanava";
            const importButtonVariant = existingImport ? "success" : "primary";
            const exportButtonVariant = existingExport ? "success" : "primary";
            return (
              <Card key={accommodation.id}>
                <Card.Header className="bg-white">
                  <div className="d-flex flex-column">
                    <span className="fw-bold">{accommodation.name}</span>
                    <span className="text-muted small">
                      {accommodation.accommodationNumber}
                    </span>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-24">
                    <div>
                      <h3 className="h6">
                        iCal viennit ulkopuolisiin kanaviin kohteelle{" "}
                        <span className="text-muted small">
                          {accommodation.accommodationNumber}
                        </span>
                      </h3>
                      <div className="border rounded p-12">
                        {renderExportRows(accommodation.id)}
                        <Form className="border-top pt-12 mt-12">
                          <Row className="g-12">
                            <Col>
                              <Form.Label>Jakelukanava</Form.Label>
                              <Form.Select
                                value={exportForm.distributionChannelId}
                                onChange={(event) => {
                                  const selectedChannelId = event.target.value;
                                  const existingExport = findExportByChannel(
                                    accommodation.id,
                                    selectedChannelId,
                                  );
                                  updateExportForm(accommodation.id, {
                                    distributionChannelId: selectedChannelId,
                                    isActive: existingExport
                                      ? existingExport.isActive
                                      : true,
                                  });
                                }}
                              >
                                <option value="">Valitse kanava</option>
                                {distributionChannels.map((channel) => (
                                  <option key={channel.id} value={channel.id}>
                                    {channel.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                          </Row>
                          <Row className="g-12 pt-8">
                            <Col>
                              <Form.Check
                                type="checkbox"
                                id={`export-active-${accommodation.id}`}
                                label="Aktiivinen"
                                checked={exportForm.isActive}
                                onChange={(event) =>
                                  updateExportForm(accommodation.id, {
                                    isActive: event.target.checked,
                                  })
                                }
                              />
                            </Col>
                          </Row>
                          <Row className="g-12 pt-8">
                            <Col>
                              <Button
                                variant={exportButtonVariant}
                                onClick={() =>
                                  handleAddExport(accommodation.id)
                                }
                                disabled={
                                  !exportForm.distributionChannelId ||
                                  isSavingExport
                                }
                              >
                                {isSavingExport
                                  ? "Tallennetaan..."
                                  : exportButtonText}
                              </Button>
                            </Col>
                          </Row>
                        </Form>
                      </div>
                    </div>
                    <div>
                      <h3 className="h6">
                        iCal tuonnit OMAliittymään kohteelle{" "}
                        <span className="text-muted small">
                          {accommodation.accommodationNumber}
                        </span>
                      </h3>
                      <div className="border rounded p-12">
                        {renderImportRows(
                          accommodation.id,
                          accommodation.accommodationNumber,
                        )}
                        <Form className="border-top pt-12 mt-12">
                          <Row className="g-12">
                            <Col>
                              <Form.Label>Jakelukanava</Form.Label>
                              <Form.Select
                                value={importForm.distributionChannelId}
                                onChange={(event) => {
                                  const selectedChannelId = event.target.value;
                                  const existingImport = findImportByChannel(
                                    accommodation.id,
                                    selectedChannelId,
                                  );
                                  updateImportForm(accommodation.id, {
                                    distributionChannelId: selectedChannelId,
                                    sourceUrl: existingImport
                                      ? existingImport.sourceUrl
                                      : "",
                                    isActive: existingImport
                                      ? existingImport.isActive
                                      : true,
                                  });
                                }}
                              >
                                <option value="">Valitse kanava</option>
                                {distributionChannels.map((channel) => (
                                  <option key={channel.id} value={channel.id}>
                                    {channel.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                          </Row>
                          <Row className="g-12 pt-8">
                            <Col>
                              <Form.Label>Lähde-URL</Form.Label>
                              <Form.Control
                                type="url"
                                placeholder="https://"
                                value={importForm.sourceUrl}
                                onChange={(event) =>
                                  updateImportForm(accommodation.id, {
                                    sourceUrl: event.target.value,
                                  })
                                }
                              />
                            </Col>
                          </Row>
                          <Row className="g-12 pt-8">
                            <Col>
                              <Form.Check
                                type="checkbox"
                                id={`import-active-${accommodation.id}`}
                                label="Aktiivinen"
                                checked={importForm.isActive}
                                onChange={(event) =>
                                  updateImportForm(accommodation.id, {
                                    isActive: event.target.checked,
                                  })
                                }
                              />
                            </Col>
                          </Row>
                          <Row className="g-12 pt-8">
                            <Col>
                              <Button
                                variant={importButtonVariant}
                                onClick={() =>
                                  handleAddImport(accommodation.id)
                                }
                                disabled={
                                  !importForm.distributionChannelId ||
                                  !importForm.sourceUrl ||
                                  isSavingImport
                                }
                              >
                                {isSavingImport
                                  ? "Tallennetaan..."
                                  : importButtonText}
                              </Button>
                            </Col>
                          </Row>
                        </Form>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      </Layout>
    </div>
  );
};

export default IcalIntegrationsPage;
